<template>
    <div class="counter-stop" :data-id="`stop-${options.stopName}`">
        <counter-header
            :stop-time="formattedTime"
            :stop-name="options.stopName"
            :booking-count="options.bookingCount"
            :edit-mode="editMode"
            :toggle-edit-mode="toggleEditMode"
            :editable="editable && !readOnly"
            :no-show="currentNoShowBooking?.bookingNoShowType"
            @no-passengers="emit('no-passengers')"
            @delete-no-show="emit('delete-no-show', currentNoShowBooking)"
        />
        <v-container class="py-0 px-3">
            <v-row v-for="(eventData, key) in options.stopEventData" :key="key" class="flex-nowrap">
                <v-col :cols="inline ? 4 : 6" class="px-0 flex-grow-0 flex-shrink-0">
                    <counter-cell
                        :count="eventData.in"
                        :edit-mode="editMode"
                        suffix="inn"
                        :disabled="disableAdd || readOnly"
                        :rider-type="key"
                        :inline="inline"
                        @input="updateInValue(key, $event)"
                    />
                </v-col>
                <v-col :cols="inline ? 4 : 6" class="px-0 flex-grow-0 flex-shrink-0">
                    <counter-cell
                        :count="eventData.out"
                        :edit-mode="editMode"
                        suffix="ut"
                        :disabled="disableRemove || readOnly"
                        :rider-type="key"
                        :inline="inline"
                        @input="updateOutValue(key, $event)"
                    />
                </v-col>
                <v-col v-if="inline" cols="4" class="px-0 flex-grow-0 flex-shrink-0">
                    <counter-sum prefix="Totalt ombord" class="inline-sum" :sums="sums" />
                </v-col>
            </v-row>
            <v-row v-if="hasCarsLeftBehind">
                <v-col cols="12" class="pa-0">
                    <counter-cell
                        :count="options.carsLeftBehind"
                        :edit-mode="editMode"
                        suffix="Kjøretøy akterutseilt"
                        rider-type="carsLeftBehind"
                        class="cars-behind"
                        :disabled="disableAdd || readOnly"
                        :inline="inline"
                        @input="updateCarsLeftBehind($event)"
                    />
                </v-col>
            </v-row>
            <v-row v-if="!inline">
                <counter-sum prefix="Totalt ombord" :sums="sums" />
            </v-row>
        </v-container>
    </div>
</template>

<script lang="ts" setup>
import CounterHeader from './CounterHeader.vue';
import CounterCell from './CounterCell.vue';
import CounterSum from './CounterSum.vue';
import { EntryMode } from '@/features/shared/models/EntryMode';
import { RiderTypes } from '@/features/shared/models/RiderType';
import { ref, computed } from 'vue';
import { PassingTime } from '../PassingTime';
import { StopTotalsWithSum } from '../Stop';
import { BookingNoShowResponseDto } from '@/features/trips/BookingNoShowModels';

const props = defineProps<{
    options: PassingTime;
    totals: StopTotalsWithSum;
    inline: boolean;
    disableAdd: boolean;
    disableRemove: boolean;
    entryMode: EntryMode;
    editable: boolean;
    readOnly: boolean;
    noShowBooking: Array<BookingNoShowResponseDto>;
}>();

const emit = defineEmits(['update', 'no-passengers', 'delete-no-show']);

const editMode = ref(props.entryMode === EntryMode.MANUAL);
const passangersIn = props.options.stopEventData?.passengers?.in ?? 0;
if (passangersIn > 0 && props.options.sequenceOrder === 1) {
    setInValue('passengers', passangersIn);
}

const currentNoShowBooking = computed(() => {
    return props.noShowBooking.find(x => {
        return (
            x.tripStop.stop.quayRef === props.options.stop.quayRef &&
            x.tripStop.stop.publicCode === props.options.stop.publicCode &&
            x.tripStop.sequenceOrder === props.options.sequenceOrder
        );
    });
});

const formattedTime = computed(() => {
    const date = new Date(props.options.passingTime);
    return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
});

function toggleEditMode() {
    editMode.value = !editMode.value;
}

const sums = computed(() => {
    const sum = [{ count: props.totals.passengers, type: 'person' }];

    if (typeof props.totals.allCars === 'number') {
        sum.push({ count: props.totals.allCars, type: 'vehicle' });
    }

    return sum;
});

const hasCarsLeftBehind = computed(() => {
    return props.options.stopEventData && Object.prototype.hasOwnProperty.call(props.options.stopEventData, RiderTypes.Cars);
});

function setInValue(key: string, value: number) {
    const model = { ...props.options };
    const data = model.stopEventData[key];
    data.in = value;
    return model;
}

function setOutValue(key: string, value: number) {
    const model = { ...props.options };
    const data = model.stopEventData[key];
    data.out = value;
    return model;
}

function updateInValue(key: string, value: number) {
    const model = setInValue(key, value);
    emit('update', model);
}

function updateOutValue(key: string, value: number) {
    const model = setOutValue(key, value);
    emit('update', model);
}

function updateCarsLeftBehind(value: number) {
    const model = {
        ...props.options,
        carsLeftBehind: value
    };

    emit('update', model);
}
</script>

<style lang="scss" scoped>
.col {
    border-right: 1px solid rgba(51, 63, 72, 0.08);

    &:last-child {
        border-right: 0 none;
    }
}

.counter-stop > .container {
    border-bottom: 1px solid rgba(51, 63, 72, 0.08);

    .row {
        border-top: 1px solid rgba(51, 63, 72, 0.08);

        &:first-child {
            border-top: 0 none;
        }
    }
}

.cars-behind {
    margin-bottom: 10px;
}
</style>
